import {
  Button,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import {
  BUSINESS_LABEL,
  BUSINESS_LIST_OPTIONS,
  COST_SUMMARY_KINDS_OPTIONS,
  COST_SUMMARY_TYPES_OPTIONS,
  SUB_RESOURCE_LIST_OPTIONS,
} from 'pages/admin/CostDashboard/constants';
import { BusinessCostSummaryFilter } from 'pages/admin/CostDashboard/hooks/useGetBusinessCostSummary';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormSelectField } from 'shared/components/form';
import TRow from 'shared/components/Table/TRow';
import {
  BusinessCostRecord,
  BusinessCostSummaryReply,
} from 'shared/types/protoc-gen/bffbillsystem';
import { ColumnConfig } from 'shared/types/table';
import { commify } from 'shared/utils/strings';

export type ResourceRankingFilter = Pick<
  BusinessCostSummaryFilter,
  'business' | 'kind' | 'type' | 'subResource'
>;

interface IResourceRankingTable extends ResourceRankingFilter {
  setResourceRankingFilter: React.Dispatch<React.SetStateAction<ResourceRankingFilter>>;
  businessCostSummary?: BusinessCostSummaryReply;
}

const ResourceRankingTable: React.FC<IResourceRankingTable> = ({
  business,
  businessCostSummary,
  kind,
  setResourceRankingFilter,
  subResource,
  type,
}) => {
  const form = useForm<IResourceRankingTable>({
    defaultValues: {
      business,
      kind,
      type,
      subResource,
    },
  });

  const handleApply = () => {
    const values = form.getValues();

    setResourceRankingFilter({
      business: values.business,
      kind: values.kind,
      type: values.type,
      subResource: values.subResource,
    });
  };

  const resourceRankingData = useMemo(() => {
    if (!businessCostSummary) return [];

    return businessCostSummary?.items.sort((a, b) => b.costCounter - a.costCounter);
  }, [businessCostSummary]);

  const columnConfig: ColumnConfig<BusinessCostRecord>[] = useMemo(
    () => [
      {
        id: 'business',
        Header: 'Business',
        Cell: row => <Typography>{BUSINESS_LABEL[row.business]}</Typography>,
      },
      {
        id: 'kind',
        Header: 'Kind',
        Cell: row => <Typography>{row.kind}</Typography>,
      },
      {
        id: 'type',
        Header: 'Type',
        Cell: row => <Typography>{row.type}</Typography>,
      },
      {
        id: 'subResource',
        Header: 'Sub Resource',
        Cell: row => <Typography>{row.subResource}</Typography>,
      },
      {
        id: 'startTime',
        Header: 'Time',
        Cell: row => <Typography>{dayjs(row.startTime).format('MMMM D, YYYY')}</Typography>,
      },
      {
        id: 'cost',
        Header: 'Cost',
        Cell: row => <Typography>${commify(row.costCounter.toFixed(2))}</Typography>,
        align: 'right',
      },
    ],
    [],
  );

  return (
    <Stack spacing={5}>
      <FormProvider {...form}>
        <form>
          <Grid container spacing={1}>
            <Grid item xs={4}>
              <Stack>
                <Typography>Business</Typography>

                <FormSelectField fieldConfig={{ name: 'business' }} items={BUSINESS_LIST_OPTIONS} />
              </Stack>
            </Grid>

            <Grid item xs={4}>
              <Stack>
                <Typography>Kind</Typography>

                <FormSelectField
                  fieldConfig={{ name: 'kind' }}
                  items={COST_SUMMARY_KINDS_OPTIONS}
                />
              </Stack>
            </Grid>

            <Grid item xs={4}>
              <Stack>
                <Typography>Type</Typography>

                <FormSelectField
                  fieldConfig={{ name: 'type' }}
                  items={COST_SUMMARY_TYPES_OPTIONS}
                />
              </Stack>
            </Grid>

            <Grid item xs={4}>
              <Stack>
                <Typography>Sub-Resource</Typography>

                <FormSelectField
                  fieldConfig={{ name: 'subResource' }}
                  items={SUB_RESOURCE_LIST_OPTIONS}
                />
              </Stack>
            </Grid>

            <Grid
              item
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
                pb: 1,
              }}
              xs={8}
            >
              <Stack alignItems="end" direction="row" justifyContent="end" sx={{ width: '100%' }}>
                <Button onClick={handleApply} sx={{ width: '160px', mb: 1 }}>
                  Apply
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </form>
      </FormProvider>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {columnConfig.map(col => (
                <TableCell align={col.align || 'left'} key={col.id} sx={{ width: col.width }}>
                  {col.Header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {resourceRankingData?.length > 0 ? (
              resourceRankingData.map(data => (
                <TRow<BusinessCostRecord>
                  columnConfig={columnConfig}
                  key={data?.id}
                  rowData={data}
                />
              ))
            ) : (
              <TableRow>
                <TableCell align="center" colSpan={columnConfig.length}>
                  <Typography sx={{ py: 2 }} variant="caption">
                    No data available
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

export default ResourceRankingTable;
